/**
 * AI Form Builder Wizard
 * 
 * Generates a form from business description, then lets user
 * fully edit fields before creation — same controls as Form Builder.
 * Steps: Describe → Generate → Edit & Review → Create
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Sparkles, Check, ArrowRight, RefreshCw, FileText, Plus, Trash2, GripVertical, Brain, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import OptionsEditor from "@/components/OptionsEditor";

const FIELD_TYPES = [
  { value: "text", label: "Short Text" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "textarea", label: "Long Text" },
  { value: "select", label: "Dropdown" },
  { value: "radio", label: "Radio Buttons" },
  { value: "checkbox", label: "Checkboxes" }
];

const FIELD_TYPE_LABELS = Object.fromEntries(FIELD_TYPES.map(t => [t.value, t.label]));

export default function AIFormBuilderWizard({
  isOpen,
  onClose,
  token,
  onCreateForm
}) {
  const [step, setStep] = useState("describe"); // describe, loading, edit
  const [businessDescription, setBusinessDescription] = useState("");
  const [formName, setFormName] = useState("");
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState("");
  const [expandedField, setExpandedField] = useState(null);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  // Persist last generation across open/close
  const [hasGenerated, setHasGenerated] = useState(false);

  const handleGenerate = async () => {
    if (!businessDescription || businessDescription.trim().length < 10) {
      toast.error("Please describe your business in more detail (at least 10 characters)");
      return;
    }
    
    setLoading(true);
    setStep("loading");
    
    try {
      const response = await axios.post(
        `${API}/forms/ai/generate`,
        {
          business_description: businessDescription,
          form_name: formName || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const data = response.data;
      setFields(data.fields?.map((f, i) => ({ ...f, id: f.id || crypto.randomUUID(), order: i })) || []);
      if (data.form_name) setFormName(data.form_name);
      if (data.summary) setSummary(data.summary);
      setHasGenerated(true);
      setStep("edit");
    } catch (error) {
      console.error("AI generation error:", error);
      toast.error(error.response?.data?.detail || "Failed to generate form. Please try again.");
      setStep("describe");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    if (fields.length === 0) {
      toast.error("Add at least one field");
      return;
    }
    
    onCreateForm({
      name: formName || "AI Generated Form",
      fields: fields.map(({ id, ...rest }) => rest),
      mode: "pre_booking"
    });
    
    handleFullClose();
  };

  const handleRegenerateRequest = () => {
    if (fields.length > 0) {
      setShowRegenerateConfirm(true);
    } else {
      doRegenerate();
    }
  };

  const doRegenerate = () => {
    setShowRegenerateConfirm(false);
    setStep("describe");
  };

  // Keep state when closing (so reopening shows last generated form)
  const handleClose = () => {
    onClose();
  };

  // Full reset
  const handleFullClose = () => {
    setStep("describe");
    setBusinessDescription("");
    setFormName("");
    setFields([]);
    setSummary("");
    setHasGenerated(false);
    setExpandedField(null);
    onClose();
  };

  // When reopening, show edit view if previously generated
  useEffect(() => {
    if (isOpen && hasGenerated && fields.length > 0 && step === "describe") {
      setStep("edit");
    }
  }, [isOpen]);

  // Field editing helpers
  const updateField = (idx, updates) => {
    setFields(prev => prev.map((f, i) => i === idx ? { ...f, ...updates } : f));
  };

  const removeField = (idx) => {
    setFields(prev => prev.filter((_, i) => i !== idx));
    setExpandedField(null);
  };

  const addField = () => {
    const newField = {
      id: crypto.randomUUID(),
      field_type: "text",
      label: "New Field",
      placeholder: "",
      required: false,
      options: null,
      include_in_score: false,
      score_weight: 0,
      order: fields.length
    };
    setFields(prev => [...prev, newField]);
    setExpandedField(fields.length);
  };

  const moveField = (idx, direction) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= fields.length) return;
    const newFields = [...fields];
    [newFields[idx], newFields[newIdx]] = [newFields[newIdx], newFields[idx]];
    setFields(newFields);
    setExpandedField(newIdx);
  };

  // Scoring stats
  const scoringFields = fields.filter(f => f.include_in_score);
  const totalWeight = scoringFields.reduce((sum, f) => sum + (f.score_weight || 0), 0);
  const needsOptions = (type) => ['select', 'radio', 'checkbox'].includes(type);

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-500" />
              Build Form with AI
            </DialogTitle>
            <DialogDescription>
              {step === "describe" && "Describe your business and goals to generate a custom lead form."}
              {step === "loading" && "Generating your form..."}
              {step === "edit" && "Review and customize your form before creating."}
            </DialogDescription>
          </DialogHeader>

          {/* Step 1: Describe */}
          {step === "describe" && (
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="ai-name">Form Name (Optional)</Label>
                <Input
                  id="ai-name"
                  placeholder="e.g., Project Lead Form"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="mt-2"
                  data-testid="ai-form-name-input"
                />
              </div>
              <div>
                <Label htmlFor="ai-description">Describe Your Business & What You Want to Collect</Label>
                <Textarea
                  id="ai-description"
                  placeholder="e.g., I run a film production company focused on female-driven sci-fi projects. I need a form to screen potential clients..."
                  value={businessDescription}
                  onChange={(e) => setBusinessDescription(e.target.value)}
                  className="mt-2"
                  rows={5}
                  data-testid="ai-description-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Be specific about what information helps you qualify good leads
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={handleClose}>Cancel</Button>
                <Button onClick={handleGenerate} disabled={businessDescription.trim().length < 10} data-testid="ai-generate-btn">
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Form
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step 2: Loading */}
          {step === "loading" && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-purple-500 mb-4" />
              <p className="text-muted-foreground">Creating your form...</p>
              <p className="text-xs text-muted-foreground mt-2">AI is designing the perfect questions for your business</p>
            </div>
          )}

          {/* Step 3: Edit & Review */}
          {step === "edit" && (
            <div className="space-y-4 py-2">
              {/* Form Name */}
              <div>
                <Label htmlFor="ai-finalName">Form Name</Label>
                <Input
                  id="ai-finalName"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="mt-1"
                  data-testid="ai-form-name-edit"
                />
              </div>

              {/* Summary */}
              {summary && (
                <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                  <p className="text-sm text-purple-800">{summary}</p>
                </div>
              )}

              {/* Scoring Stats */}
              <div className="flex items-center gap-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div>
                  <p className="text-xs text-blue-600">Scoring Fields</p>
                  <p className="text-lg font-bold text-blue-800">{scoringFields.length}</p>
                </div>
                <div>
                  <p className="text-xs text-blue-600">Total Weight</p>
                  <p className={`text-lg font-bold ${totalWeight === 100 ? 'text-green-600' : 'text-amber-600'}`}>
                    {totalWeight}%
                    {totalWeight === 100 && <Check className="inline h-4 w-4 ml-1" />}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-blue-600">Total Fields</p>
                  <p className="text-lg font-bold text-blue-800">{fields.length}</p>
                </div>
              </div>

              {/* Editable Fields */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Fields</Label>
                  <Button variant="outline" size="sm" onClick={addField} data-testid="ai-add-field-btn">
                    <Plus className="w-3 h-3 mr-1" /> Add Field
                  </Button>
                </div>
                <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
                  {fields.map((field, idx) => (
                    <Card key={field.id || idx} className={`bg-gray-50 transition-all ${expandedField === idx ? 'ring-1 ring-primary/30' : ''}`}>
                      <CardContent className="p-3">
                        {/* Collapsed Row */}
                        <div 
                          className="flex items-center gap-2 cursor-pointer"
                          onClick={() => setExpandedField(expandedField === idx ? null : idx)}
                        >
                          <GripVertical className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm truncate">{field.label}</span>
                              {field.required && <span className="text-red-500 text-xs">*</span>}
                            </div>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <Badge variant="outline" className="text-xs">
                                {FIELD_TYPE_LABELS[field.field_type] || field.field_type}
                              </Badge>
                              {field.include_in_score && (
                                <Badge className="bg-blue-100 text-blue-800 text-xs">
                                  <Brain className="w-3 h-3 mr-0.5" />{field.score_weight}%
                                </Badge>
                              )}
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); removeField(idx); }} className="text-red-400 hover:text-red-600 h-7 w-7 p-0" data-testid={`ai-delete-field-${idx}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                          {expandedField === idx ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                        </div>

                        {/* Expanded Edit */}
                        {expandedField === idx && (
                          <div className="mt-3 pt-3 border-t space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs">Label</Label>
                                <Input
                                  value={field.label}
                                  onChange={(e) => updateField(idx, { label: e.target.value })}
                                  className="mt-1 h-8 text-sm"
                                  data-testid={`ai-field-label-${idx}`}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Type</Label>
                                <Select value={field.field_type} onValueChange={(v) => updateField(idx, { field_type: v, options: needsOptions(v) && !field.options ? ["Option 1"] : field.options })}>
                                  <SelectTrigger className="mt-1 h-8 text-sm" data-testid={`ai-field-type-${idx}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {FIELD_TYPES.map(t => (
                                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            
                            <div>
                              <Label className="text-xs">Placeholder</Label>
                              <Input
                                value={field.placeholder || ""}
                                onChange={(e) => updateField(idx, { placeholder: e.target.value })}
                                className="mt-1 h-8 text-sm"
                                placeholder="Placeholder text..."
                                data-testid={`ai-field-placeholder-${idx}`}
                              />
                            </div>

                            {/* Options for select/radio/checkbox — uses shared editor (no cursor jumps + per-option scoring) */}
                            {needsOptions(field.field_type) && (
                              <OptionsEditor
                                field={field}
                                index={idx}
                                updateField={updateField}
                                testIdPrefix="ai-field"
                              />
                            )}

                            <div className="flex items-center gap-6">
                              <label className="flex items-center gap-2 text-xs">
                                <Switch
                                  checked={field.required}
                                  onCheckedChange={(v) => updateField(idx, { required: v })}
                                  className="scale-75"
                                  data-testid={`ai-field-required-${idx}`}
                                />
                                Required
                              </label>
                              <label className="flex items-center gap-2 text-xs">
                                <Switch
                                  checked={field.include_in_score}
                                  onCheckedChange={(v) => updateField(idx, { include_in_score: v, score_weight: v ? (field.score_weight || 10) : 0 })}
                                  className="scale-75"
                                  data-testid={`ai-field-scoring-${idx}`}
                                />
                                <Brain className="w-3 h-3" /> AI Scoring
                              </label>
                            </div>

                            {field.include_in_score && (
                              <div className="flex items-center gap-2">
                                <Label className="text-xs whitespace-nowrap">Weight</Label>
                                <Input
                                  type="number"
                                  min={0}
                                  max={100}
                                  value={field.score_weight || 0}
                                  onChange={(e) => updateField(idx, { score_weight: parseInt(e.target.value) || 0 })}
                                  className="h-8 text-sm w-20"
                                  data-testid={`ai-field-weight-${idx}`}
                                />
                                <span className="text-xs text-muted-foreground">%</span>
                              </div>
                            )}

                            {/* Move controls */}
                            <div className="flex gap-1 pt-1">
                              <Button variant="ghost" size="sm" disabled={idx === 0} onClick={() => moveField(idx, -1)} className="h-7 text-xs">
                                <ChevronUp className="w-3 h-3 mr-1" /> Up
                              </Button>
                              <Button variant="ghost" size="sm" disabled={idx === fields.length - 1} onClick={() => moveField(idx, 1)} className="h-7 text-xs">
                                <ChevronDown className="w-3 h-3 mr-1" /> Down
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                  {fields.length === 0 && (
                    <div className="text-center py-6 text-muted-foreground text-sm">
                      No fields yet. Click "Add Field" to get started.
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter className="flex gap-2 pt-4">
                <Button variant="outline" onClick={handleRegenerateRequest} data-testid="ai-regenerate-btn">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Regenerate
                </Button>
                <Button variant="outline" onClick={handleClose}>Cancel</Button>
                <Button onClick={handleCreate} disabled={fields.length === 0 || (scoringFields.length > 0 && totalWeight !== 100)} data-testid="ai-create-form-btn">
                  <FileText className="h-4 w-4 mr-2" />
                  Create Form
                </Button>
              </DialogFooter>
              
              {scoringFields.length > 0 && totalWeight !== 100 && (
                <p className="text-xs text-amber-600 text-center">
                  Scoring weights total {totalWeight}% — adjust to 100% before creating
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Regenerate Confirmation */}
      <AlertDialog open={showRegenerateConfirm} onOpenChange={setShowRegenerateConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate form?</AlertDialogTitle>
            <AlertDialogDescription>
              This will replace your current fields with a new AI-generated form. Any edits you've made will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={doRegenerate}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Regenerate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
